import { Component, Suspense, lazy } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

const LandingPage = lazy(() =>
  import('@/routes/LandingPage').then((m) => ({ default: m.LandingPage }))
);
const LoginPage = lazy(() =>
  import('@/routes/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const AppShell = lazy(() =>
  import('@/routes/AppShell').then((m) => ({ default: m.AppShell }))
);
const AdminPage = lazy(() =>
  import('@/routes/AdminPage').then((m) => ({ default: m.AdminPage }))
);
const ChatView = lazy(() =>
  import('@/components/chat/ChatView').then((m) => ({ default: m.ChatView }))
);
const AgentViewLoader = lazy(() =>
  import('@/components/agents/AgentViewLoader').then((m) => ({
    default: m.AgentViewLoader,
  }))
);

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary:', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '40px', background: '#06060e', color: '#e0e0f0', minHeight: '100vh', fontFamily: 'monospace' }}>
          <h1 style={{ color: '#f87171' }}>⚠️ 에러</h1>
          <pre style={{ background: '#1a1a2e', padding: '16px', borderRadius: '8px', overflow: 'auto', fontSize: '13px', whiteSpace: 'pre-wrap' }}>
            {String(this.state.error.message)}{'\n\n'}{String(this.state.error.stack || '')}
          </pre>
          <button
            onClick={() => this.setState({ error: null })}
            style={{ marginTop: '16px', padding: '8px 16px', background: '#7c5cfc', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
          >
            다시 시도
          </button>
          {' '}
          <a href="/app" style={{ color: '#00d4ff' }}>← 기존 화면</a>
        </div>
      );
    }
    return this.props.children;
  }
}

function Fallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#06060e', color: '#7878a0' }}>
      로딩 중...
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<Fallback />}>
          <Routes>
            {/* Landing page */}
            <Route path="/" element={<LandingPage />} />

            {/* Login page */}
            <Route path="/login" element={<LoginPage />} />

            {/* Main app shell with sidebar + content */}
            <Route path="/app" element={<AppShell />}>
              {/* Default: chat view */}
              <Route index element={<ChatView />} />
              {/* Agent views */}
              <Route path="agent/:agentId" element={<AgentViewLoader />} />
            </Route>

            {/* Admin page */}
            <Route path="/admin" element={<AdminPage />} />

            {/* Catch-all: redirect to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
